import * as THREE from 'three'

export type SfxKind = 'chime' | 'bell' | 'drum' | 'wave' | 'wind' | 'whoosh' | 'stamp'

/** WebAudio合成音（方位パン対応）。素材音源が来るまでの手続き音 */
export class Sfx {
  private ctx?: AudioContext
  private master?: GainNode
  private readonly worldScale = 0.09

  /** ユーザー操作後に一度呼ぶ（autoplay制限対応） */
  ensure(): void {
    if (this.ctx) {
      if (this.ctx.state === 'suspended') void this.ctx.resume()
      return
    }
    const AC = window.AudioContext ?? (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext
    this.ctx = new AC()
    this.master = this.ctx.createGain()
    this.master.gain.value = 0.55
    this.master.connect(this.ctx.destination)
  }

  play(kind: SfxKind, worldPos?: THREE.Vector3): void {
    if (!this.ctx || !this.master) return
    const ctx = this.ctx
    let out: AudioNode = this.master
    if (worldPos) {
      const p = ctx.createPanner()
      p.panningModel = 'HRTF'
      p.distanceModel = 'inverse'
      p.refDistance = 3
      p.positionX.value = worldPos.x * this.worldScale
      p.positionY.value = worldPos.y * this.worldScale
      p.positionZ.value = worldPos.z * this.worldScale
      p.connect(this.master)
      out = p
    }
    const t = ctx.currentTime
    switch (kind) {
      case 'chime': this.tone(out, t, 1568, 0.9, 0.16, 'sine'); this.tone(out, t + 0.02, 2352, 0.5, 0.08, 'sine'); break
      case 'bell': this.tone(out, t, 523, 1.6, 0.2, 'triangle'); this.tone(out, t, 1319, 1.2, 0.1, 'sine'); break
      case 'drum': this.noise(out, t, 0.18, 220, 0.5); this.pitchDrop(out, t, 130, 55, 0.25, 0.5); break
      case 'wave': this.noise(out, t, 2.6, 480, 0.22, 0.9); break
      case 'wind': this.noise(out, t, 2.2, 900, 0.15, 0.7); break
      case 'whoosh': this.noise(out, t, 0.55, 1600, 0.5, 0.1, true); break
      case 'stamp': this.noise(out, t, 0.07, 300, 0.6); this.tone(out, t, 180, 0.12, 0.3, 'square'); break
    }
  }

  private tone(out: AudioNode, t: number, freq: number, dur: number, vol: number, type: OscillatorType): void {
    const ctx = this.ctx!
    const o = ctx.createOscillator()
    const g = ctx.createGain()
    o.type = type
    o.frequency.value = freq
    g.gain.setValueAtTime(vol, t)
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur)
    o.connect(g).connect(out)
    o.start(t)
    o.stop(t + dur + 0.05)
  }

  private pitchDrop(out: AudioNode, t: number, f0: number, f1: number, dur: number, vol: number): void {
    const ctx = this.ctx!
    const o = ctx.createOscillator()
    const g = ctx.createGain()
    o.type = 'sine'
    o.frequency.setValueAtTime(f0, t)
    o.frequency.exponentialRampToValueAtTime(f1, t + dur)
    g.gain.setValueAtTime(vol, t)
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur)
    o.connect(g).connect(out)
    o.start(t)
    o.stop(t + dur + 0.05)
  }

  private noise(out: AudioNode, t: number, dur: number, cutoff: number, vol: number, attack = 0.01, sweep = false): void {
    const ctx = this.ctx!
    const len = Math.ceil(ctx.sampleRate * dur)
    const buf = ctx.createBuffer(1, len, ctx.sampleRate)
    const d = buf.getChannelData(0)
    for (let i = 0; i < len; i++) d[i] = Math.random() * 2 - 1
    const src = ctx.createBufferSource()
    src.buffer = buf
    const f = ctx.createBiquadFilter()
    f.type = sweep ? 'bandpass' : 'lowpass'
    f.frequency.setValueAtTime(cutoff, t)
    if (sweep) f.frequency.exponentialRampToValueAtTime(cutoff * 4, t + dur)
    const g = ctx.createGain()
    g.gain.setValueAtTime(0.0001, t)
    g.gain.linearRampToValueAtTime(vol, t + attack)
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur)
    src.connect(f).connect(g).connect(out)
    src.start(t)
  }
}
